if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define(
    'cart-drawer-recommendations',
    class CartDrawerRecommendations extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-recommendation-track]');
        this.slides = Array.from(this.querySelectorAll('[data-recommendation-slide]'));
        this.dots = Array.from(this.querySelectorAll('.cart-recommendations__dots span'));
        this.activeIndex = 0;
        this.isVisible = false;
        this.isPaused = false;

        if (!this.track || this.slides.length < 2 || this.dots.length === 0) return;

        this.updateActiveDot = this.updateActiveDot.bind(this);
        this.pauseAutoplay = this.pauseAutoplay.bind(this);
        this.resumeAutoplay = this.resumeAutoplay.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

        this.track.addEventListener('scroll', this.updateActiveDot, { passive: true });
        this.addEventListener('mouseenter', this.pauseAutoplay);
        this.addEventListener('mouseleave', this.resumeAutoplay);
        this.addEventListener('focusin', this.pauseAutoplay);
        this.addEventListener('focusout', this.resumeAutoplay);
        this.addEventListener('touchstart', this.pauseAutoplay, { passive: true });
        this.addEventListener('touchend', this.resumeAutoplay, { passive: true });
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(([entry]) => {
            this.isVisible = entry.isIntersecting;
            this.updateAutoplay();
          }, { threshold: 0.6 });
          this.observer.observe(this);
        } else {
          this.isVisible = true;
          this.updateAutoplay();
        }
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.observer?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.track && this.updateActiveDot) this.track.removeEventListener('scroll', this.updateActiveDot);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }

      pauseAutoplay() {
        this.isPaused = true;
        this.stopAutoplay();
        this.classList.remove('is-autoplaying');
      }

      resumeAutoplay() {
        this.isPaused = false;
        this.updateAutoplay();
      }

      handleVisibilityChange() {
        this.updateAutoplay();
      }

      updateAutoplay() {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const shouldPlay = this.isVisible && !this.isPaused && !document.hidden && !reducedMotion;
        if (!shouldPlay) {
          this.stopAutoplay();
          this.classList.remove('is-autoplaying');
          return;
        }

        this.stopAutoplay();
        this.restartProgressAnimation();
        this.autoplayTimer = window.setInterval(() => this.showNextSlide(), 4000);
      }

      restartProgressAnimation() {
        this.classList.remove('is-autoplaying');
        void this.offsetWidth;
        this.classList.add('is-autoplaying');
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      showNextSlide() {
        const nextIndex = (this.activeIndex + 1) % this.slides.length;
        this.setActiveIndex(nextIndex);
        this.track.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior: 'smooth' });
      }

      setActiveIndex(index) {
        this.activeIndex = index;
        this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      }

      updateActiveDot() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const activeIndex = this.slides.reduce((closestIndex, slide, index) => {
            const currentDistance = Math.abs(slide.offsetLeft - this.track.scrollLeft);
            const closestDistance = Math.abs(this.slides[closestIndex].offsetLeft - this.track.scrollLeft);
            return currentDistance < closestDistance ? index : closestIndex;
          }, 0);

          if (activeIndex !== this.activeIndex) this.setActiveIndex(activeIndex);
        });
      }
    }
  );
}
